import { Trans, useLingui } from "@lingui/react/macro";

import { cn } from "@/utils/shadcn-ui";

import type { GDMTMedicationResult, GDMTResult } from "../../../api";

import { useSupportedMedications } from "../../../catalogs/supported-medications";
import { flagHint } from "./gdmt";
import { type GdmtSlot, gdmtSlots, slotColor } from "./gdmt-slots";

/**
 * The GDMT pillars as a fixed grid. Shared by the medication-tab card and the
 * profile-header popover. Every pillar that counts for the patient's HF type
 * keeps its place, so a class the patient isn't on reads as a gap rather than
 * being absent from the page.
 */
export function GdmtBreakdown({ result }: { result: GDMTResult }) {
  const { t } = useLingui();
  const slots = gdmtSlots(result, useSupportedMedications());

  // Container queries (not viewport breakpoints): the same grid renders inside
  // the full-width medication-tab card and the ~480px profile popover.
  return (
    <div className="@container">
      {slots.length > 0 ? (
        <ul
          aria-label={t`GDMT drug classes`}
          className="grid grid-cols-1 gap-3 @sm:grid-cols-2"
        >
          {slots.map((slot) => (
            <GdmtSlotCell key={slot.key} slot={slot} />
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground text-sm">
          <Trans>No GDMT drug classes apply to this HF type.</Trans>
        </p>
      )}

      {result.weight_missing && (
        <p className="text-muted-foreground/80 mt-3 text-xs">
          <Trans>
            Patient weight missing — weight-dependent targets fall back to the
            lowest band.
          </Trans>
        </p>
      )}
    </div>
  );
}

function GdmtSlotCell({ slot }: { slot: GdmtSlot }) {
  const { i18n } = useLingui();
  const color = slotColor[slot.state];

  return (
    <li
      className={cn("min-w-0 rounded-md border border-l-2 p-2", color.border)}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-muted-foreground min-w-0 text-xs font-semibold tracking-wide break-words uppercase">
          {i18n._(slot.label)}
        </span>
        <span
          className={cn(
            "flex shrink-0 items-center gap-1 text-sm font-semibold",
            color.text,
          )}
        >
          <span className={cn("size-2 rounded-full", color.bg)} />
          {slot.points}/{slot.maxPoints}
        </span>
      </div>

      {slot.drugs.length === 0 ? (
        <div className={cn("mt-1 text-sm font-medium", color.text)}>
          <Trans>Not prescribed</Trans>
        </div>
      ) : (
        slot.drugs.map((drug, index) => (
          <GdmtDrug
            // The API can repeat a class (e.g. two ARNI entries), so the class
            // name alone is not a unique key.
            key={`${drug.drug_class}-${index}`}
            drug={drug}
            color={color}
          />
        ))
      )}
    </li>
  );
}

function GdmtDrug({
  drug,
  color,
}: {
  drug: GDMTMedicationResult;
  color: { text: string; bg: string };
}) {
  const { i18n } = useLingui();
  const hint = flagHint(drug.flag);
  const doseProgress =
    drug.daily_dose != null &&
    drug.target_daily_dose != null &&
    drug.target_daily_dose > 0
      ? Math.min(1, drug.daily_dose / drug.target_daily_dose)
      : null;
  // A drug in a scoring class that earns nothing is greyed apart from the ones
  // carrying the slot, rather than being dropped from the card.
  const counted = drug.points > 0;

  return (
    <div className="mt-1.5">
      {/* `drug_name` is nullable in the contract; the class label above already
          identifies the row, so drop the line rather than leaving it blank. */}
      {drug.drug_name != null && (
        <div
          className={cn(
            "font-medium break-words",
            counted ? "text-foreground" : "text-muted-foreground",
          )}
        >
          {drug.drug_name}
        </div>
      )}

      {drug.daily_dose != null && (
        <div className="text-muted-foreground text-xs">
          <Trans>
            {drug.daily_dose} {drug.daily_dose_unit ?? ""}/day
          </Trans>
          {drug.target_daily_dose != null && (
            <>
              {" · "}
              <Trans>target {drug.target_daily_dose}</Trans>
            </>
          )}
        </div>
      )}

      {doseProgress != null && counted && (
        <div className="bg-muted-foreground/20 mt-1 h-1.5 w-full rounded-full">
          {/* Keep a sliver of fill visible for tiny fractions of the target
              dose, so the bar doesn't degrade into a bare grey line. */}
          <div
            className={cn("h-1.5 rounded-full", color.bg)}
            style={{ width: `${Math.max(3, Math.round(doseProgress * 100))}%` }}
          />
        </div>
      )}

      {hint != null && (
        <div className="text-muted-foreground/80 text-xs">{i18n._(hint)}</div>
      )}
    </div>
  );
}
